import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CopyToClipboard } from '@delmonicos/shared-design-system-react/src/components/copy-to-clipboard/copy-to-clipboard';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import {
  Icon,
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { NumberFixed } from '@delmonicos/shared-design-system-react/src/components/number-fixed/number-fixed';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { Tag } from '@delmonicos/shared-design-system-react/src/components/tag/tag';
import { MiniTitle } from '@delmonicos/shared-design-system-react/src/components/typography/mini-title/mini-title';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { toCurrencyString } from '@delmonicos/shared-sdk/libs/core/src/utils/formatters/currency.formatters';
import { formatDate } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/format-date';
import { getDuration } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/formatters/get-duration';
import { useTranslation } from 'react-i18next';

import i18n from '@app/core/locale/i18n';

import { useEmspBoChargingSessionContext } from '../../emsp-bo-charging-session.context';

export const SessionInfoCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargingSession = useEmspBoChargingSessionContext();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card flex={1}>
      <Stack direction="column" gap={GapEnum.L}>
        <AccentTitle
          icon="info-circle"
          text={t('$.emsp-bo-charging-session-details.session-info-card.title')}
          as={HeadingEnum.H2}
          variant={AccentVariantEnum.Accent2}
        />
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="electric-car-2"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charging-session.session-id')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <CopyToClipboard textToCopy={chargingSession.sessionId}>
              <Text>{chargingSession.sessionId}</Text>
            </CopyToClipboard>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="clock-fast-forward"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t(
                '$.emsp-bo-charging-session-details.session-info-card.duration',
              )}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Tag
              className="bg-surface-secondary"
              flex={1}
              justifyContent="center">
              {getDuration(
                chargingSession.startedAt,
                chargingSession.stoppedAt,
              )}
            </Tag>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="calendar"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charging-session.started-at')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Text>{formatDate(chargingSession.startedAt, i18n)}</Text>
          </Stack>
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="calendar-check-02"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charging-session.stopped-at')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Text>
              {chargingSession.stoppedAt
                ? formatDate(chargingSession.stoppedAt, i18n)
                : '-'}
            </Text>
          </Stack>
        </Stack>
        <Divider />
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="lightning-02"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t(
                'delmonicos-shared-sdk.outputs-charging-session.energy-delivered',
              )}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Tag
              className="bg-surface-secondary"
              flex={1}
              justifyContent="center">
              {chargingSession.energyDelivered !== undefined ? (
                <NumberFixed number={chargingSession.energyDelivered}>
                  kWh
                </NumberFixed>
              ) : (
                '-'
              )}
            </Tag>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="currency-euro-circle"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charging-session.total-ht')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Tag
              className="bg-surface-secondary"
              flex={1}
              justifyContent="center">
              {chargingSession.totalHT !== undefined
                ? toCurrencyString(chargingSession.totalHT)
                : '-'}
            </Tag>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="space-between">
          <Stack alignItems="center" gap={GapEnum.XS} flex={1}>
            <Icon
              color={IconColor.Secondary}
              iconSize={IconSize.Small}
              name="currency-euro-circle"
            />
            <MiniTitle color={TextColor.Secondary}>
              {t('delmonicos-shared-sdk.outputs-charging-session.total-ttc')}
            </MiniTitle>
          </Stack>
          <Stack flex={1}>
            <Tag
              className="bg-surface-secondary"
              flex={1}
              justifyContent="center">
              {chargingSession.totalTTC !== undefined
                ? toCurrencyString(chargingSession.totalTTC)
                : '-'}
            </Tag>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
